import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLocaleFormatters } from '@/lib/format';
import { ApiError } from '@wrapforce/shared/errors';
import { apiErrorMessage } from '@/lib/api-errors';
import { useExecuteAction } from './api';
import type { PendingAction } from './types';

type CardState = 'pending' | 'executing' | 'done' | 'dismissed' | 'expired';

// Recognized payload fields, mapped to their i18n label key. Anything else in
// the payload is skipped rather than rendered as a raw key.
const FIELD_LABEL_KEYS: Record<string, string> = {
  name: 'field.name',
  fullName: 'field.name',
  phone: 'field.phone',
  email: 'field.email',
  year: 'field.year',
  make: 'field.make',
  model: 'field.model',
  delta: 'field.delta',
  reason: 'field.reason',
  notes: 'field.notes',
};

// Any payload field ending in "Cents" is a money amount and gets formatted
// with formatMoney rather than shown as a raw key/value pair.
const CENTS_SUFFIX = 'Cents';

export function ActionCard({
  action,
  workspaceId,
  onOpenLink,
}: {
  action: PendingAction;
  workspaceId: string | null;
  onOpenLink: (deepLink: string) => void;
}) {
  const { t } = useTranslation('assistant');
  const { formatMoney } = useLocaleFormatters();
  const [state, setState] = useState<CardState>('pending');
  const [deepLink, setDeepLink] = useState<string | null>(null);
  const executeAction = useExecuteAction(workspaceId);

  const fields: { label: string; value: string }[] = [];
  for (const [key, raw] of Object.entries(action.payload)) {
    if (raw === null || raw === undefined || raw === '') continue;
    if (key.endsWith(CENTS_SUFFIX) && typeof raw === 'number') {
      fields.push({ label: t('field.price'), value: formatMoney(raw) });
      continue;
    }
    const labelKey = FIELD_LABEL_KEYS[key];
    if (!labelKey) continue;
    const value = key === 'delta' && typeof raw === 'number' ? `${raw > 0 ? '+' : ''}${raw}` : String(raw);
    fields.push({ label: t(labelKey), value });
  }

  const handleConfirm = () => {
    setState('executing');
    executeAction.mutate(action.id, {
      onSuccess: (result) => {
        setState('done');
        setDeepLink(result.deepLink);
        toast.success(t('actionDone'));
      },
      onError: (err) => {
        if (err instanceof ApiError && err.code === 'NOT_FOUND') {
          setState('expired');
          toast.error(t('actionExpired'));
        } else {
          setState('pending');
          toast.error(t('actionError'), { description: apiErrorMessage(err) });
        }
      },
    });
  };

  const handleDismiss = () => setState('dismissed');

  return (
    <Card className="mt-2">
      <CardContent className="space-y-2 p-3">
        <p className="text-sm text-ink">{action.summary}</p>
        {fields.length > 0 ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-muted">
            {fields.map((f, i) => (
              <div key={i} className="contents">
                <dt className="font-semibold">{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {state === 'pending' ? (
          <div className="flex gap-2 pt-1">
            <Button variant="primary" size="sm" onClick={handleConfirm}>
              {t('action.confirm')}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDismiss}>
              {t('action.dismiss')}
            </Button>
          </div>
        ) : null}
        {state === 'executing' ? (
          <Button variant="primary" size="sm" loading disabled>
            {t('action.executing')}
          </Button>
        ) : null}
        {state === 'done' ? (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs font-semibold text-accent-ink">{t('action.done')}</span>
            {deepLink ? (
              <Button variant="outline" size="sm" onClick={() => onOpenLink(deepLink)}>
                {t('action.open')}
              </Button>
            ) : null}
          </div>
        ) : null}
        {state === 'dismissed' ? <p className="text-xs text-muted">{t('action.dismiss')}</p> : null}
        {state === 'expired' ? <p className="text-xs text-danger">{t('action.expired')}</p> : null}
      </CardContent>
    </Card>
  );
}
